'use strict'

const KMZ_GIMBAL_SCOPE_PRIORITY = {
  playback: ['waylines', 'template'],
  templateExport: ['template', 'waylines'],
  waylinesExport: ['waylines', 'template']
}

const KMZ_GIMBAL_ROOT_CACHE_PATHS = [
  'wpml:gimbalPitchMode',
  'gimbalPitchMode',
  'Folder.wpml:gimbalPitchMode',
  'Folder.gimbalPitchMode',
  'wpml:gimbalPitchAngle',
  'gimbalPitchAngle',
  'wpml:waypointGimbalHeadingParam',
  'waypointGimbalHeadingParam'
]

const KMZ_GIMBAL_SCOPED_CACHE_PATHS = [
  'wpml:gimbalPitchMode',
  'gimbalPitchMode',
  'Folder.wpml:gimbalPitchMode',
  'Folder.gimbalPitchMode',
  'wpml:gimbalPitchAngle',
  'gimbalPitchAngle',
  'wpml:waypointGimbalHeadingParam',
  'waypointGimbalHeadingParam'
]

const KMZ_GIMBAL_WAYLINES_PARAM_CHILDREN = [
  'waypointGimbalPitchAngle',
  'waypointGimbalYawAngle'
]

const fnCloneGimbalValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneGimbalValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneGimbalValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneGimbalValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedGimbalSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeGimbalSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedGimbalSource(out, 'Folder.wpml:gimbalPitchMode', pointProps, [
    'Folder.wpml:gimbalPitchMode',
    'Folder.gimbalPitchMode',
    'wpml:gimbalPitchMode',
    'gimbalPitchMode'
  ])
  fnAssignScopedGimbalSource(out, 'Folder.wpml:gimbalPitchMode', folderProps, [
    'wpml:gimbalPitchMode',
    'gimbalPitchMode',
    'Folder.wpml:gimbalPitchMode',
    'Folder.gimbalPitchMode'
  ])
  fnAssignScopedGimbalSource(out, 'wpml:gimbalPitchAngle', pointProps, [
    'wpml:gimbalPitchAngle',
    'gimbalPitchAngle'
  ])
  fnAssignScopedGimbalSource(out, 'wpml:waypointGimbalHeadingParam', pointProps, [
    'wpml:waypointGimbalHeadingParam',
    'waypointGimbalHeadingParam'
  ])
  return out
}

const fnPruneKmzGimbalCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_GIMBAL_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_GIMBAL_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedGimbalPropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeGimbalSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeGimbalSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedGimbalProps = function (props, scopedGimbalProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedGimbalProps && typeof scopedGimbalProps === 'object') ? scopedGimbalProps : {}
  fnPruneKmzGimbalCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedGimbalSource(safeProps, scopeId + '.Folder.wpml:gimbalPitchMode', scopeProps, [
      'Folder.wpml:gimbalPitchMode',
      'Folder.gimbalPitchMode',
      'wpml:gimbalPitchMode',
      'gimbalPitchMode'
    ])
    fnAssignScopedGimbalSource(safeProps, scopeId + '.wpml:gimbalPitchAngle', scopeProps, [
      'wpml:gimbalPitchAngle',
      'gimbalPitchAngle'
    ])
    fnAssignScopedGimbalSource(safeProps, scopeId + '.wpml:waypointGimbalHeadingParam', scopeProps, [
      'wpml:waypointGimbalHeadingParam',
      'waypointGimbalHeadingParam'
    ])
  })
  return safeProps
}

const syncKmzPointGimbalScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedGimbalPropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedGimbalProps(point.props, scoped)
  return point
}

const syncKmzMissionGimbalScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointGimbalScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeGimbalPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  if (normalized.toLowerCase().indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzGimbalFieldPath = function (path) {
  const normalized = fnNormalizeGimbalPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:gimbalpitchmode' ||
    normalized === 'gimbalpitchmode' ||
    normalized === 'folder.wpml:gimbalpitchmode' ||
    normalized === 'folder.gimbalpitchmode' ||
    normalized === 'wpml:gimbalpitchangle' ||
    normalized === 'gimbalpitchangle' ||
    normalized.indexOf('wpml:waypointgimbalheadingparam') === 0 ||
    normalized.indexOf('waypointgimbalheadingparam') === 0
}

const isKmzGlobalGimbalFieldPath = function (path) {
  const normalized = fnNormalizeGimbalPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:gimbalpitchmode' ||
    normalized === 'gimbalpitchmode' ||
    normalized === 'folder.wpml:gimbalpitchmode' ||
    normalized === 'folder.gimbalpitchmode'
}

const fnResolveGimbalCandidateByUseCase = function (pointOrProps, useCase, mode, childName, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_GIMBAL_SCOPE_PRIORITY[useCase] || KMZ_GIMBAL_SCOPE_PRIORITY.playback
  const safeChild = String(childName || '').trim()
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    let paths = []
    if (mode === 'globalMode') {
      paths = [
        scopeId + '.Folder.wpml:gimbalPitchMode',
        scopeId + '.Folder.gimbalPitchMode',
        scopeId + '.wpml:gimbalPitchMode',
        scopeId + '.gimbalPitchMode'
      ]
    } else if (mode === 'templatePitch') {
      paths = [
        scopeId + '.wpml:gimbalPitchAngle',
        scopeId + '.gimbalPitchAngle'
      ]
    } else if (mode === 'waylinesGimbalChild' && safeChild) {
      paths = [
        scopeId + '.wpml:waypointGimbalHeadingParam.wpml:' + safeChild,
        scopeId + '.wpml:waypointGimbalHeadingParam.' + safeChild,
        scopeId + '.waypointGimbalHeadingParam.wpml:' + safeChild,
        scopeId + '.waypointGimbalHeadingParam.' + safeChild
      ]
    }
    for (let j = 0; j < paths.length; j += 1) {
      const value = fnGetByPath(props, paths[j])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return {
        value: value,
        scopeId: scopeId,
        path: paths[j]
      }
    }
  }
  return {
    value: fallbackValue,
    scopeId: '',
    path: ''
  }
}

const fnResolveGimbalValueByUseCase = function (pointOrProps, useCase, mode, childName, fallbackValue) {
  return fnResolveGimbalCandidateByUseCase(pointOrProps, useCase, mode, childName, fallbackValue).value
}

const resolveKmzTemplateExportGimbalMode = function (pointOrProps, fallbackValue) {
  return fnResolveGimbalValueByUseCase(pointOrProps, 'templateExport', 'globalMode', '', fallbackValue)
}

const resolveKmzTemplateExportGimbalTemplatePitch = function (pointOrProps, fallbackValue) {
  return fnResolveGimbalValueByUseCase(pointOrProps, 'templateExport', 'templatePitch', '', fallbackValue)
}

const resolveKmzWaylinesExportGimbalField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveGimbalValueByUseCase(pointOrProps, 'waylinesExport', 'waylinesGimbalChild', childName, fallbackValue)
}

const applyKmzResolvedGimbalDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  const globalMode = fnResolveGimbalValueByUseCase(point, safeUseCase, 'globalMode', '', undefined)
  const templatePitch = fnResolveGimbalValueByUseCase(point, safeUseCase, 'templatePitch', '', undefined)
  if (globalMode !== undefined) {
    targetMap['wpml:gimbalPitchMode'] = globalMode
  }
  if (templatePitch !== undefined) {
    targetMap['wpml:gimbalPitchAngle'] = templatePitch
  }
  KMZ_GIMBAL_WAYLINES_PARAM_CHILDREN.forEach((childName) => {
    const value = fnResolveGimbalValueByUseCase(point, safeUseCase, 'waylinesGimbalChild', childName, undefined)
    if (value !== undefined) {
      targetMap['wpml:waypointGimbalHeadingParam.wpml:' + childName] = value
    }
  })
  return targetMap
}

export {
  buildKmzScopedGimbalPropsFromSources,
  applyKmzScopedGimbalProps,
  syncKmzPointGimbalScopesFromXml,
  syncKmzMissionGimbalScopesFromXml,
  isKmzGimbalFieldPath,
  isKmzGlobalGimbalFieldPath,
  resolveKmzTemplateExportGimbalMode,
  resolveKmzTemplateExportGimbalTemplatePitch,
  resolveKmzWaylinesExportGimbalField,
  applyKmzResolvedGimbalDisplayValuesToMap
}
